import { useFormik } from 'formik'
import { useState } from 'react'
import { NumericFormat } from 'react-number-format'
import { PRIMARY_COLOR } from '../../constants'
import { parseCurrencyToNumber } from '../../helpers'
import {
  useGetCreatorSettlementConfigurationQuery,
  useUpdateCreatorSettlementMutation,
} from '../../redux-toolkits/vendors/vendors.slice'
import {
  CreatorSettlementPeriod,
  CreatorSettlementSchedule,
} from '../../redux-toolkits/vendors/vendors.types'
import { Vendor } from '../../redux-toolkits/vendors/vendors.types'
import { VendorSettlementConfigurationSchema } from '../../validation/validate'
import { Checkbox } from '../inputs/Checkbox'
import { EditInput } from '../inputs/editInput'
import { TextInput } from '../inputs/textInput'
import { LoadingOval } from '../spinner/Spinner'
import CustSwitch from '../switch'
import { TextSubtext } from '../typography/textSubtext'

const getSettlementPeriodFromSchedule = (
  schedule: CreatorSettlementSchedule | undefined,
): CreatorSettlementPeriod | undefined => {
  if (schedule === 'INSTANT') {
    return 'DAY'
  } else if (schedule === 'TIME_BASED') {
    return 'DAY'
  }

  return undefined
}

export const VendorSettlementConfiguration = ({
  vendor,
  className = '',
  refetch,
}: {
  vendor?: Vendor
  className?: string
  refetch: () => void
}) => {
  const [editing, setEditing] = useState(false)

  const {
    data: vendorSettlementConfig,
    isFetching: fetchingvendorSettlementConfig,
  } = useGetCreatorSettlementConfigurationQuery(
    {
      id: vendor?._id ?? '',
    },
    { refetchOnMountOrArgChange: true },
  )

  const [updateConfiguration, { isLoading: updatingConfiguration }] =
    useUpdateCreatorSettlementMutation()

  const {
    values,
    setFieldValue,
    handleChange,
    handleBlur,
    handleSubmit,
    errors,
    touched,
  } = useFormik({
    initialValues: {
      settlementSchedule: vendorSettlementConfig?.data?.settlementSchedule,
      settlementPeriod:
        vendorSettlementConfig?.data?.settlementPeriod ??
        getSettlementPeriodFromSchedule(
          vendorSettlementConfig?.data?.settlementSchedule,
        ),
      settlementEnabled: vendorSettlementConfig?.data?.settlementEnabled,
      settlementType: vendorSettlementConfig?.data?.settlementType,
      settlementDelayDays: vendorSettlementConfig?.data?.settlementDelayDays,
    },
    enableReinitialize: true,
    validationSchema: VendorSettlementConfigurationSchema,
    onSubmit: async (vals) => {
      const resp = await updateConfiguration({
        id: vendor?._id ?? '',
        payload: {
          settlementSchedule: vals.settlementSchedule,
          settlementPeriod: vals.settlementPeriod,
          settlementEnabled: vals.settlementEnabled,
          settlementType: vals.settlementType,
          settlementDelayDays:
            vals.settlementSchedule === 'TIME_BASED'
              ? parseCurrencyToNumber(vals.settlementDelayDays)
              : 0,
        },
      })

      if (resp.data?.success) {
        setEditing(false)
      }
    },
  })

  return (
    <div className={className}>
      {fetchingvendorSettlementConfig ? (
        <div className="absolute bg-transparent inset-0 flex items-center justify-center">
          <LoadingOval
            loaderHeight="25"
            loaderWidth="25"
            color={PRIMARY_COLOR}
          />
        </div>
      ) : (
        <EditInput
          onEditSave={() => {
            handleSubmit()
          }}
          editing={editing}
          onEditClick={() => {
            setEditing(true)
          }}
          onCancel={() => {
            setEditing(false)
          }}
          title="Settlement Configuration"
          loading={updatingConfiguration}
        >
          {({ isEditing }) => {
            return isEditing ? (
              <div className="w-full flex flex-col gap-6">
                <div className="flex items-center gap-6 border-b pb-4">
                  <TextSubtext
                    text="Settlement"
                    subtext="When enabled, funds from purchases of this creator’s items will be settled into their account. When disabled, settlements are paused."
                    className="flex-1"
                  />

                  <CustSwitch
                    checked={values.settlementEnabled ?? false}
                    disabled={false}
                    onChange={(checked) => {
                      setFieldValue('settlementEnabled', checked)
                    }}
                  />
                </div>

                <div className="flex flex-col gap-4 border-b pb-4">
                  <TextSubtext
                    text="Settlement type"
                    subtext="Choose when creator settlement should be triggered"
                    className="flex-1"
                  />

                  <Checkbox
                    checkStatus={values.settlementType === 'PAYMENT'}
                    disabled={false}
                    handleChange={() => {
                      setFieldValue('settlementType', 'PAYMENT')
                    }}
                    text={'After payment is made and successfully confirmed'}
                    textPosition="left"
                    className="w-full"
                  />

                  <Checkbox
                    checkStatus={values.settlementType === 'POST_DELIVERY'}
                    disabled={false}
                    handleChange={() => {
                      setFieldValue('settlementType', 'POST_DELIVERY')
                    }}
                    text={'After delivery is made and successfully confirmed'}
                    textPosition="left"
                    className="w-full"
                  />
                </div>

                <div className="flex flex-col gap-4 border-b pb-4">
                  <TextSubtext
                    text="Settlement schedule"
                    subtext="Choose when a creator should be settled"
                    className="flex-1"
                  />

                  <Checkbox
                    checkStatus={values.settlementSchedule === 'INSTANT'}
                    disabled={false}
                    handleChange={() => {
                      setFieldValue('settlementSchedule', 'INSTANT')
                      setFieldValue('settlementPeriod', 'DAY')
                    }}
                    text={'Instant'}
                    textPosition="left"
                    className="w-full"
                  />

                  <Checkbox
                    checkStatus={
                      values.settlementSchedule === 'PERIOD_BASED' &&
                      values.settlementPeriod === 'DAY'
                    }
                    disabled={false}
                    handleChange={() => {
                      setFieldValue('settlementSchedule', 'PERIOD_BASED')
                      setFieldValue('settlementPeriod', 'DAY')
                    }}
                    text={'At the end of the day'}
                    textPosition="left"
                    className="w-full"
                  />

                  <Checkbox
                    checkStatus={
                      values.settlementSchedule === 'PERIOD_BASED' &&
                      values.settlementPeriod === 'WEEK'
                    }
                    disabled={false}
                    handleChange={() => {
                      setFieldValue('settlementSchedule', 'PERIOD_BASED')
                      setFieldValue('settlementPeriod', 'WEEK')
                    }}
                    text={'At the end of the week'}
                    textPosition="left"
                    className="w-full"
                  />

                  <Checkbox
                    checkStatus={
                      values.settlementSchedule === 'PERIOD_BASED' &&
                      values.settlementPeriod === 'MONTH'
                    }
                    disabled={false}
                    handleChange={() => {
                      setFieldValue('settlementSchedule', 'PERIOD_BASED')
                      setFieldValue('settlementPeriod', 'MONTH')
                    }}
                    text={'At the end of the month'}
                    textPosition="left"
                    className="w-full"
                  />

                  <Checkbox
                    checkStatus={values.settlementSchedule === 'TIME_BASED'}
                    disabled={false}
                    handleChange={() => {
                      setFieldValue('settlementSchedule', 'TIME_BASED')
                      setFieldValue('settlementPeriod', 'DAY')
                    }}
                    text={'Custom schedule'}
                    textPosition="left"
                    className="w-full"
                  />

                  {values.settlementSchedule === 'TIME_BASED' ? (
                    <NumericFormat
                      suffix={undefined}
                      customInput={TextInput}
                      type="text"
                      name="settlementDelayDays"
                      placeholder="Enter number of days"
                      value={values.settlementDelayDays}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      errors={
                        errors.settlementDelayDays &&
                        touched.settlementDelayDays
                          ? errors.settlementDelayDays
                          : ''
                      }
                      hasIcon={false}
                      decimalScale={2}
                      thousandSeparator
                    />
                  ) : undefined}
                </div>
              </div>
            ) : (
              <div>
                {vendorSettlementConfig?.data?.settlementEnabled
                  ? 'Enabled'
                  : 'Disabled'}
              </div>
            )
          }}
        </EditInput>
      )}
    </div>
  )
}
